<template>
    <div class="knowledge-base-detail">
        <div class="header-section">
            <div class="title-wrapper">
                <div class="left-section">
                    <a-button @click="goBack" icon="arrow-left" class="mr-3">Geri</a-button>
                    <h1 class="main-title" v-if="knowledgeBase">{{ knowledgeBase.knowledge_base_name }}</h1>
                </div>
                <div class="status-section" v-if="knowledgeBase">
                    <a-tooltip title="Bu bilgi tabanı tamamlandı">
                        <a-badge :status="getStatusBadge(knowledgeBase.status)" :text="getStatusText(knowledgeBase.status)" />
                    </a-tooltip>
                    <a-tooltip :title="knowledgeBase.enable_auto_refresh ? 'Otomatik Yenileme Aktif' : 'Otomatik Yenileme Kapalı'">
                        <a-tag color="blue" v-if="knowledgeBase.enable_auto_refresh">
                            <a-icon type="sync" /> Otomatik Yenileme
                        </a-tag>
                        <a-tag color="orange" v-else>
                            <a-icon type="pause-circle" /> Manuel Yenileme
                        </a-tag>
                    </a-tooltip>
                </div>
            </div>
            <p class="title-description">Bilgi tabanı detayları ve kaynak yönetimi</p>
        </div>

        <a-spin :spinning="loading">
            <div class="section" v-if="knowledgeBase">
                <!-- Kaynak Ekleme Butonu -->
                <div class="sources-header">
                    <h2>Kaynaklar</h2>
                    <a-button type="primary" icon="plus" @click="showAddSourceModal">
                        Yeni Kaynak Ekle
                    </a-button>
                </div>

                <!-- URL Kaynakları -->
                <div class="source-section">
                    <h3>URL Kaynakları</h3>
                    <a-table
                        :columns="urlColumns"
                        :dataSource="urlSources"
                        rowKey="source_id"
                        :pagination="{ pageSize: 5 }"
                        size="middle"
                        :locale="{emptyText: 'URL kaynağı bulunmamaktadır'}"
                    >
                        <template slot="action" slot-scope="text, record">
                            <a-button
                                type="danger"
                                shape="circle"
                                icon="delete"
                                size="small"
                                @click="deleteSource(knowledgeBase.knowledge_base_id, record.source_id)"
                            />
                        </template>
                    </a-table>
                </div>

                <!-- Dosya Kaynakları -->
                <div class="source-section">
                    <h3>Dosya Kaynakları</h3>
                    <a-table
                        :columns="fileColumns"
                        :dataSource="fileSources"
                        rowKey="source_id"
                        :pagination="{ pageSize: 5 }"
                        size="middle"
                        :locale="{emptyText: 'Dosya kaynağı bulunmamaktadır'}"
                    >
                        <template slot="action" slot-scope="text, record">
                            <a-button
                                type="primary"
                                shape="circle"
                                icon="eye"
                                size="small"
                                class="mr-2"
                                @click="openFileUrl(record.file_url)"
                            />
                            <a-button
                                type="danger"
                                shape="circle"
                                icon="delete"
                                size="small"
                                @click="deleteSource(knowledgeBase.knowledge_base_id, record.source_id)"
                            />
                        </template>
                    </a-table>
                </div>

                <!-- Metin Kaynakları -->
                <div class="source-section">
                    <h3>Metin Kaynakları</h3>
                    <a-table
                        :columns="textColumns"
                        :dataSource="textSources"
                        rowKey="source_id"
                        :pagination="{ pageSize: 5 }"
                        size="middle"
                        :locale="{emptyText: 'Metin kaynağı bulunmamaktadır'}"
                    >
                        <template slot="action" slot-scope="text, record">
                            <a-button
                                type="danger"
                                shape="circle"
                                icon="delete"
                                size="small"
                                @click="deleteSource(knowledgeBase.knowledge_base_id, record.source_id)"
                            />
                        </template>
                    </a-table>
                </div>
            </div>
        </a-spin>

        <!-- Kaynak Ekleme Modal -->
        <a-modal
            v-model="showSourceModal"
            title="Yeni Kaynak Ekle"
            :footer="null"
            @cancel="closeSourceModal"
            width="700px"
        >
            <a-tabs default-active-key="url">
                <a-tab-pane key="url" tab="URL Ekle">
                    <a-form :form="urlForm" layout="vertical" @submit="handleUrlSubmit">
                        <a-form-item label="URL'ler (Her satıra bir URL ekleyin)">
                            <a-textarea
                                v-decorator="[
                                    'knowledge_base_urls',
                                    {
                                        rules: [{ required: true, message: 'Lütfen en az bir URL girin' }]
                                    }
                                ]"
                                placeholder="https://www.example.com"
                                :rows="4"
                            />
                        </a-form-item>

                        <div class="button-container">
                            <a-button class="mr-2" @click="closeSourceModal">
                                İptal
                            </a-button>
                            <a-button type="primary" html-type="submit" :loading="submitting">
                                Ekle
                            </a-button>
                        </div>
                    </a-form>
                </a-tab-pane>

                <a-tab-pane key="file" tab="Dosya Yükle">
                    <a-form :form="fileForm" layout="vertical" @submit="handleFileSubmit">
                        <a-form-item label="Dosya Yükle">
                            <a-upload-dragger
                                name="files"
                                :multiple="true"
                                :fileList="fileList"
                                @change="handleFileChange"
                                :beforeUpload="beforeUpload"
                            >
                                <p class="ant-upload-drag-icon">
                                    <a-icon type="inbox" />
                                </p>
                                <p class="ant-upload-text">Dosyaları sürükleyip bırakın veya tıklayarak seçin</p>
                                <p class="ant-upload-hint">
                                    PDF, DOC, DOCX, TXT, CSV gibi dosyaları yükleyebilirsiniz
                                </p>
                            </a-upload-dragger>
                        </a-form-item>

                        <div class="button-container">
                            <a-button class="mr-2" @click="closeSourceModal">
                                İptal
                            </a-button>
                            <a-button 
                                type="primary" 
                                html-type="submit" 
                                :loading="submitting"
                                :disabled="!fileList.length"
                            >
                                Ekle
                            </a-button>
                        </div>
                    </a-form>
                </a-tab-pane>

                <a-tab-pane key="text" tab="Metin Ekle">
                    <a-form :form="textForm" layout="vertical" @submit="handleTextSubmit">
                        <div v-for="(text, index) in sourceTexts" :key="index" class="text-item">
                            <a-row :gutter="16">
                                <a-col :span="8">
                                    <a-form-item label="Başlık" :required="true">
                                        <a-input
                                            v-model="text.title"
                                            placeholder="Başlık"
                                        />
                                    </a-form-item>
                                </a-col>
                                <a-col :span="14">
                                    <a-form-item label="Metin İçeriği" :required="true">
                                        <a-textarea
                                            v-model="text.text"
                                            placeholder="Metin İçeriği"
                                            :rows="3"
                                        />
                                    </a-form-item>
                                </a-col>
                                <a-col :span="2" style="display: flex; align-items: center; padding-top: 40px;">
                                    <a-button
                                        shape="circle"
                                        type="danger"
                                        icon="delete"
                                        @click="removeSourceText(index)"
                                    />
                                </a-col>
                            </a-row>
                        </div>
                        <a-button type="dashed" block @click="addSourceText" class="mb-3">
                            <a-icon type="plus" /> Metin Ekle
                        </a-button>

                        <div class="button-container">
                            <a-button class="mr-2" @click="closeSourceModal">
                                İptal
                            </a-button>
                            <a-button 
                                type="primary" 
                                html-type="submit" 
                                :loading="submitting"
                                :disabled="!isValidText"
                            >
                                Ekle
                            </a-button>
                        </div>
                    </a-form>
                </a-tab-pane>
            </a-tabs>
        </a-modal>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../api/voiceAgent";
import _ from "lodash";
import moment from "moment";

export default {
    name: "KnowledgeBaseDetail",
    props: {
        channelId: {
            type: String,
            required: true
        },
        knowledgeBaseId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            knowledgeBase: null,
            loading: false,
            submitting: false,
            showSourceModal: false,
            fileList: [],
            sourceTexts: [],
            urlForm: this.$form.createForm(this, { name: 'url_form' }),
            fileForm: this.$form.createForm(this, { name: 'file_form' }),
            textForm: this.$form.createForm(this, { name: 'text_form' }),
            urlColumns: [
                {
                    title: 'URL',
                    dataIndex: 'url',
                    key: 'url',
                    ellipsis: true,
                },
                {
                    title: 'İşlemler',
                    key: 'action',
                    scopedSlots: { customRender: 'action' },
                    width: 100
                }
            ],
            fileColumns: [
                {
                    title: 'Dosya Adı',
                    dataIndex: 'filename',
                    key: 'filename',
                    ellipsis: true,
                },
                {
                    title: 'İşlemler',
                    key: 'action',
                    scopedSlots: { customRender: 'action' },
                    width: 100
                }
            ],
            textColumns: [
                {
                    title: 'Başlık',
                    dataIndex: 'title',
                    key: 'title',
                    ellipsis: true,
                },
                {
                    title: 'İçerik',
                    dataIndex: 'text',
                    key: 'text',
                    ellipsis: true,
                },
                {
                    title: 'İşlemler',
                    key: 'action',
                    scopedSlots: { customRender: 'action' },
                    width: 100
                }
            ]
        };
    },
    computed: {
        urlSources() {
            if (!this.knowledgeBase || !this.knowledgeBase.knowledge_base_sources) return [];
            return this.knowledgeBase.knowledge_base_sources.filter(source => 
                source.type === 'url' || (source.filename && source.filename.startsWith('http'))
            );
        },
        fileSources() {
            if (!this.knowledgeBase || !this.knowledgeBase.knowledge_base_sources) return [];
            return this.knowledgeBase.knowledge_base_sources.filter(source => 
                source.type === 'document' && (!source.filename || !source.filename.startsWith('http'))
            );
        },
        textSources() {
            if (!this.knowledgeBase || !this.knowledgeBase.knowledge_base_sources) return [];
            return this.knowledgeBase.knowledge_base_sources.filter(source => 
                source.type === 'text'
            );
        },
        isValidText() {
            return this.sourceTexts.length > 0 && 
                  this.sourceTexts.every(text => text.title && text.title.trim() && text.text && text.text.trim());
        }
    },
    watch: {
        knowledgeBaseId: {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.fetchKnowledgeBase();
                }
            }
        }
    },
    methods: {
        formatDate(timestamp) {
            return moment(new Date(timestamp)).format('DD.MM.YYYY HH:mm');
        },
        goBack() {
            // Üst bileşene geri gitme olayını bildir
            this.$emit('back');
        },
        async fetchKnowledgeBase() {
            try {
                this.loading = true;
                const knowledgeBase = await VOICE_AGENT_API.knowledgeBases.get(this.knowledgeBaseId);
                this.knowledgeBase = knowledgeBase;
                this.sourceTexts = [];
            } catch (error) {
                console.error("Bilgi tabanı detayları çekilirken hata oluştu:", error);
                this.$message.error("Bilgi tabanı detayları yüklenirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        getStatusBadge(status) {
            if (status === 'complete') return 'success';
            if (status === 'in_progress') return 'processing';
            if (status === 'error') return 'error';
            return 'default';
        },
        getStatusColor(status) {
            if (status === 'complete') return 'green';
            if (status === 'in_progress') return 'blue';
            if (status === 'error') return 'red';
            return 'default';
        },
        getStatusText(status) {
            if (status === 'complete') return 'Tamamlandı';
            if (status === 'in_progress') return 'İşleniyor';
            if (status === 'error') return 'Hata';
            return status;
        },
        showAddSourceModal() {
            this.showSourceModal = true;
            this.sourceTexts = [{ title: '', text: '' }];
            this.fileList = [];
            this.urlForm.resetFields();
            this.fileForm.resetFields();
            this.textForm.resetFields();
        },
        closeSourceModal() {
            this.showSourceModal = false;
            this.sourceTexts = [];
            this.fileList = [];
        },
        async deleteSource(knowledgeBaseId, sourceId) {
            try {
                await this.$confirm({
                    title: "Kaynağı Sil",
                    content: "Bu kaynağı silmek istediğinize emin misiniz?",
                    okText: "Evet",
                    okType: "danger",
                    cancelText: "Hayır",
                    onOk: async () => {
                        this.loading = true;
                        await VOICE_AGENT_API.knowledgeBases.deleteSource(knowledgeBaseId, sourceId);
                        
                        // Yeniden bilgi tabanı detaylarını çek
                        await this.fetchKnowledgeBase();
                        
                        this.$message.success("Kaynak başarıyla silindi");
                    },
                });
            } catch (error) {
                console.error("Kaynak silinirken hata oluştu:", error);
                this.$message.error("Kaynak silinirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        beforeUpload(file) {
            // Otomatik yüklemeyi engelle ve dosyayı fileList'e ekle
            return false;
        },
        
        handleFileChange(info) {
            this.fileList = info.fileList;
        },
        
        addSourceText() {
            this.sourceTexts.push({ title: '', text: '' });
        },
        
        removeSourceText(index) {
            this.sourceTexts.splice(index, 1);
            if (this.sourceTexts.length === 0) {
                this.addSourceText();
            }
        },
        
        async handleUrlSubmit(e) {
            e.preventDefault();
            this.urlForm.validateFields(async (err, values) => {
                if (!err) {
                    this.submitting = true;
                    
                    try {
                        // URL'leri satır bazında ayır
                        const urls = values.knowledge_base_urls ? 
                            values.knowledge_base_urls.split('\n').filter(url => url.trim()) : 
                            [];
                        
                        if (urls.length === 0) {
                            this.$message.error("Lütfen en az bir URL girin");
                            this.submitting = false;
                            return;
                        }
                        
                        const data = {
                            knowledge_base_urls: urls,
                            knowledge_base_texts: [],
                            knowledge_base_files: []
                        };
                        
                        await VOICE_AGENT_API.knowledgeBases.addSources(this.knowledgeBase.knowledge_base_id, data);
                        
                        // Yeniden bilgi tabanı detaylarını çek
                        await this.fetchKnowledgeBase();
                        
                        this.$message.success("URL kaynakları başarıyla eklendi");
                        this.closeSourceModal();
                    } catch (error) {
                        console.error("URL kaynakları eklenirken hata oluştu:", error);
                        this.$message.error("URL kaynakları eklenirken bir hata oluştu");
                    } finally {
                        this.submitting = false;
                    }
                }
            });
        },
        
        async handleFileSubmit(e) {
            e.preventDefault();
            
            if (this.fileList.length === 0) {
                this.$message.error("Lütfen en az bir dosya yükleyin");
                return;
            }
            
            this.submitting = true;
            
            try {
                // Dosyaları hazırla
                const files = this.fileList.map(file => file.originFileObj);
                
                const data = {
                    knowledge_base_urls: [],
                    knowledge_base_texts: [],
                    knowledge_base_files: files
                };
                
                await VOICE_AGENT_API.knowledgeBases.addSources(this.knowledgeBase.knowledge_base_id, data);
                
                // Yeniden bilgi tabanı detaylarını çek
                await this.fetchKnowledgeBase();
                
                this.$message.success("Dosya kaynakları başarıyla eklendi");
                this.closeSourceModal();
            } catch (error) {
                console.error("Dosya kaynakları eklenirken hata oluştu:", error);
                this.$message.error("Dosya kaynakları eklenirken bir hata oluştu");
            } finally {
                this.submitting = false;
            }
        },
        
        async handleTextSubmit(e) {
            e.preventDefault();
            
            if (!this.isValidText) {
                this.$message.error("Lütfen en az bir metin kaynağı ekleyin");
                return;
            }
            
            this.submitting = true;
            
            try {
                // Geçerli metinleri filtrele
                const validTexts = this.sourceTexts.filter(t => t.title && t.text);
                
                const data = {
                    knowledge_base_urls: [],
                    knowledge_base_texts: validTexts,
                    knowledge_base_files: []
                };
                
                await VOICE_AGENT_API.knowledgeBases.addSources(this.knowledgeBase.knowledge_base_id, data);
                
                // Yeniden bilgi tabanı detaylarını çek
                await this.fetchKnowledgeBase();
                
                this.$message.success("Metin kaynakları başarıyla eklendi");
                this.closeSourceModal();
            } catch (error) {
                console.error("Metin kaynakları eklenirken hata oluştu:", error);
                this.$message.error("Metin kaynakları eklenirken bir hata oluştu");
            } finally {
                this.submitting = false;
            }
        },
        
        openFileUrl(fileUrl) {
            if (fileUrl) {
                window.open(fileUrl, '_blank');
            }
        }
    }
};
</script>

<style scoped lang="scss">
.knowledge-base-detail {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .header-section {
        margin-bottom: 20px;
    }

    .title-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        .left-section {
            display: flex;
            align-items: center;
            gap: 15px;
        }

        .status-section {
            display: flex;
            align-items: center;
            gap: 15px;
        }
    }

    .main-title {
        font-size: 24px;
        font-weight: 500;
        margin: 0;
    }

    .title-description {
        font-size: 14px;
        font-weight: 400;
        color: #8c8c8c;
        margin: 0;
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 25px;
    }

    .sources-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        h2 {
            margin: 0;
        }
    }

    .source-section {
        margin-bottom: 20px;
        
        h3 {
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 1px solid #f0f0f0;
        }
    }

    .button-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
    }

    .text-item {
        margin-bottom: 10px;
        padding: 15px;
        background: #f9f9f9;
        border-radius: 4px;
    }
}
</style> 